import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hasProgrammeToken, programmeClientId, programmeRequest, setProgrammeToken } from './programmeApi';

let scriptPromise;
function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    }).catch(error => {
      scriptPromise = null;
      throw error;
    });
  }
  return scriptPromise;
}

const apiValue = value => value === undefined || value === null ? 'Not returned' : String(value);

export default function ProgrammeSignIn({ children }) {
  const location = useLocation();
  const [signedIn, setSignedIn] = useState(hasProgrammeToken);
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!signedIn) return undefined;
    let active = true;
    setChecking(true);
    programmeRequest('/api/me')
      .then(profile => {
        if (active) {
          setMe(profile);
          setError('');
        }
      })
      .catch(err => {
        setProgrammeToken('');
        if (active) {
          setMe(null);
          setSignedIn(false);
          setError(err.message);
        }
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => { active = false; };
  }, [signedIn]);

  useEffect(() => {
    if (signedIn) return undefined;
    let active = true;
    loadGoogle().then(() => {
      if (!active) return;
      window.google.accounts.id.initialize({
        client_id: programmeClientId(),
        callback: ({ credential }) => {
          if (!active || !credential) return;
          setProgrammeToken(credential);
          setMe(null);
          setError('');
          setSignedIn(true);
        },
      });
      const target = document.getElementById('programme-google-sign-in');
      if (target) {
        target.replaceChildren();
        window.google.accounts.id.renderButton(target, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
        });
      }
    }).catch(() => {
      if (active) setError('Google sign-in could not load. Please try again.');
    });
    return () => { active = false; };
  }, [signedIn]);

  const onSessionExpired = () => {
    setProgrammeToken('');
    setMe(null);
    setSignedIn(false);
  };

  if (checking) {
    return <div className="min-h-screen bg-brand-light pt-28 text-center text-text-muted">Checking programme sign-in…</div>;
  }

  if (me && location.pathname === '/programme/sign-in-check') {
    return (
      <div className="min-h-screen bg-brand-light pt-28 px-4">
        <div className="max-w-md mx-auto rounded-2xl border border-brand-dark bg-white p-8">
          <h1 className="font-serif text-2xl font-bold text-accent-navy">Sign-in check</h1>
          <dl className="mt-5 space-y-3 text-sm">
            <div>
              <dt className="font-bold text-accent-navy">Email</dt>
              <dd className="text-text-main">{apiValue(me.email)}</dd>
            </div>
            <div>
              <dt className="font-bold text-accent-navy">Admin</dt>
              <dd className="text-text-main">{apiValue(me.admin)}</dd>
            </div>
            <div>
              <dt className="font-bold text-accent-navy">Rights admin</dt>
              <dd className="text-text-main">{apiValue(me.rightsAdmin)}</dd>
            </div>
          </dl>
        </div>
      </div>
    );
  }

  if (me) {
    return React.isValidElement(children)
      ? React.cloneElement(children, { onSessionExpired })
      : children;
  }

  return (
    <div className="min-h-screen bg-brand-light pt-28 px-4">
      <div className="max-w-md mx-auto rounded-2xl border border-brand-dark bg-white p-8">
        <h1 className="font-serif text-2xl font-bold text-accent-navy">Service Programme sign-in</h1>
        <p className="text-sm text-text-muted my-4">Sign in with Google to manage your service programmes.</p>
        <div id="programme-google-sign-in" />
        {error && <p role="alert" className="text-sm text-red-600 mt-4">{error}</p>}
      </div>
    </div>
  );
}